import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDemoData } from '../components/useDemoData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  ShieldCheck, CheckCircle2, XCircle, AlertTriangle, Clock,
  FileText, Download, Shield, Globe, Lock, Server,
  Users, Eye, BarChart3, ArrowRight, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import EmptyDataState from '../components/EmptyDataState';

const DEMO_COMPLIANCE = {
  summary: {
    overallScore: 78,
    prevScore: 64,
    frameworks: 3,
    controlsMet: 42,
    controlsTotal: 54,
    criticalGaps: 3,
    evidenceGenerated: 38,
    lastAuditSaved: '3 weeks',
  },
  frameworks: [
    {
      name: 'SOC 2 Type II', score: 82, status: 'on-track',
      categories: [
        { name: 'Access Controls', met: 8, total: 9, status: 'pass', details: 'RBAC enforced, MFA enabled, session management configured' },
        { name: 'Cost Governance', met: 6, total: 7, status: 'pass', details: 'Budget alerts, approval workflows, anomaly detection active' },
        { name: 'Change Management', met: 5, total: 6, status: 'pass', details: 'Audit trails for all cost changes, infrastructure modifications logged' },
        { name: 'Data Protection', met: 4, total: 5, status: 'warning', details: 'Encryption at rest/transit confirmed. Data residency for 2 regions pending verification' },
        { name: 'Incident Response', met: 3, total: 4, status: 'warning', details: 'Anomaly alerts configured. Formal incident playbook for cost breaches needs documentation' },
      ],
    },
    {
      name: 'ISO 27001', score: 74, status: 'needs-attention',
      categories: [
        { name: 'Asset Management', met: 7, total: 9, status: 'warning', details: 'Resource tagging at 53%. 865 untagged resources need ownership assignment' },
        { name: 'Supplier Relationships', met: 4, total: 5, status: 'pass', details: 'Cloud vendor contracts documented, SLAs tracked, renewal alerts configured' },
        { name: 'Operations Security', met: 5, total: 6, status: 'pass', details: 'Cost monitoring 24/7, automated alerts, segregation of duties in billing' },
        { name: 'Compliance', met: 3, total: 4, status: 'warning', details: 'Periodic cost reviews documented. Formal compliance calendar needs setup' },
      ],
    },
    {
      name: 'GDPR (Data Residency)', score: 81, status: 'on-track',
      categories: [
        { name: 'Data Mapping', met: 4, total: 5, status: 'pass', details: 'Cloud resources mapped by region. EU data stays in eu-west-1 and westeurope' },
        { name: 'Cross-Border Transfers', met: 3, total: 4, status: 'warning', details: 'S3 cross-region replication flagged — data flowing EU to US for 3 buckets' },
        { name: 'Right to Erasure', met: 3, total: 3, status: 'pass', details: 'Data lifecycle policies confirmed. Automated cleanup for expired resources' },
      ],
    },
  ],
  criticalGaps: [
    { framework: 'SOC 2', gap: 'Incident response playbook for cost anomalies not documented', impact: 'Auditor finding — needs formal runbook', fix: 'Auto-generate from existing anomaly alert configurations', effort: 'Low' },
    { framework: 'ISO 27001', gap: '865 untagged resources lack ownership assignment', impact: 'Asset management control failure', fix: 'Use Smart Tag Fixer to auto-tag with 90%+ accuracy', effort: 'Low' },
    { framework: 'GDPR', gap: '3 S3 buckets replicating EU data to US region', impact: 'Potential cross-border transfer violation', fix: 'Disable cross-region replication or add EU-only bucket policy', effort: 'Medium' },
  ],
  auditReadyEvidence: [
    { type: 'Cost Approval Audit Trail', records: 1247, period: 'Q1 2026', status: 'ready' },
    { type: 'Anomaly Detection Logs', records: 89, period: 'Q1 2026', status: 'ready' },
    { type: 'Budget Alert History', records: 34, period: 'Q1 2026', status: 'ready' },
    { type: 'User Access Reviews', records: 12, period: 'Q1 2026', status: 'ready' },
    { type: 'Resource Tag Compliance Report', records: 1847, period: 'Current', status: 'ready' },
    { type: 'Vendor Contract Register', records: 4, period: 'Current', status: 'ready' },
    { type: 'Data Residency Map', records: 6, period: 'Current', status: 'ready' },
    { type: 'Incident Response Playbook', records: 0, period: '-', status: 'missing' },
  ],
};

const ComplianceMapper = () => {
  const { shouldShowData } = useDemoData();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeFramework, setActiveFramework] = useState(0);

  useEffect(() => {
    // Iter84al — real backend; rich local demo as fallback.
    let cancelled = false;
    (async () => {
      if (!shouldShowData) { setLoading(false); return; }
      try {
        const token = localStorage.getItem('token');
        await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/finops/v2/compliance-mapper/audit`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) { setData(DEMO_COMPLIANCE); setLoading(false); }
      } catch (_e) {
        if (!cancelled) { setData(DEMO_COMPLIANCE); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [shouldShowData]);

  if (!shouldShowData) {
    return <EmptyDataState title="Cloud Compliance Mapper" description="Bridge cloud costs and compliance. Auto-generate audit-ready evidence for SOC 2, ISO 27001, and GDPR from your existing cloud data." icon={ShieldCheck} />;
  }

  if (loading) return <div className="p-6 flex items-center justify-center min-h-[400px]"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  const fw = data.frameworks[activeFramework];
  const statusIcon = (s) => ({ pass: <CheckCircle2 className="w-4 h-4 text-emerald-500" />, warning: <AlertTriangle className="w-4 h-4 text-amber-500" />, fail: <XCircle className="w-4 h-4 text-red-500" /> }[s]);

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="compliance-mapper-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-emerald-400" />
            Cloud Compliance <span className="text-emerald-400">Mapper</span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">Turn your cloud cost data into audit-ready compliance evidence.</p>
        </div>
        <Button className="bg-emerald-500 hover:bg-sky-600" onClick={() => toast.success('Compliance report exported!')} data-testid="export-compliance-btn">
          <Download className="w-4 h-4 mr-2" />Export Audit Package
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><p className="text-xs text-slate-400 mb-1">Overall Score</p><p className="text-2xl font-bold text-slate-900">{data.summary.overallScore}%</p><p className="text-xs text-emerald-500">+{data.summary.overallScore - data.summary.prevScore}% from last audit</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-400 mb-1">Controls Met</p><p className="text-2xl font-bold text-emerald-600">{data.summary.controlsMet}/{data.summary.controlsTotal}</p><p className="text-xs text-slate-400">across {data.summary.frameworks} frameworks</p></CardContent></Card>
        <Card className="border-red-200"><CardContent className="p-4"><p className="text-xs text-red-500 mb-1">Critical Gaps</p><p className="text-2xl font-bold text-red-600">{data.summary.criticalGaps}</p><p className="text-xs text-red-400">need immediate fix</p></CardContent></Card>
        <Card className="bg-emerald-50 border-emerald-200"><CardContent className="p-4"><p className="text-xs text-emerald-600 mb-1">Audit Time Saved</p><p className="text-2xl font-bold text-emerald-700">{data.summary.lastAuditSaved}</p><p className="text-xs text-emerald-500">{data.summary.evidenceGenerated} evidence items auto-generated</p></CardContent></Card>
      </div>

      {/* Framework Tabs */}
      <div className="flex gap-3">
        {data.frameworks.map((f, i) => (
          <button key={i} onClick={() => setActiveFramework(i)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeFramework === i ? 'bg-emerald-500 text-white shadow-lg' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`}>
            {f.name} <span className={`ml-1 text-xs ${activeFramework === i ? 'text-white/70' : 'text-slate-400'}`}>{f.score}%</span>
          </button>
        ))}
      </div>

      {/* Framework Details */}
      <Card>
        <CardHeader><CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2"><Shield className="w-5 h-5 text-sky-500" />{fw.name} Controls</span>
          <Badge className={`${fw.status === 'on-track' ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30' : 'bg-amber-500/10 text-amber-600 border-amber-500/30'}`}>{fw.status === 'on-track' ? 'On Track' : 'Needs Attention'}</Badge>
        </CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {fw.categories.map((cat, i) => (
            <div key={i} className={`p-4 rounded-xl border ${cat.status === 'warning' ? 'bg-amber-50 border-amber-100' : 'bg-white border-slate-100'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">{statusIcon(cat.status)}<span className="font-medium text-slate-800">{cat.name}</span></div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-400">{cat.met}/{cat.total} controls</span>
                  <Progress value={(cat.met / cat.total) * 100} className="w-24 h-2" />
                </div>
              </div>
              <p className="text-xs text-slate-400 ml-6">{cat.details}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Critical Gaps + Audit Evidence */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-500" />Critical Gaps</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {data.criticalGaps.map((gap, i) => (
              <div key={i} className="p-4 rounded-xl bg-red-50 border border-red-100">
                <div className="flex items-center justify-between mb-1">
                  <Badge variant="outline" className="text-xs">{gap.framework}</Badge>
                  <Badge className="bg-slate-100 text-slate-400 border-0 text-[11px]">Effort: {gap.effort}</Badge>
                </div>
                <p className="text-sm font-medium text-slate-800 mt-2">{gap.gap}</p>
                <p className="text-xs text-slate-400 mt-1">Impact: {gap.impact}</p>
                <div className="mt-2 p-2 bg-emerald-50 rounded-lg"><p className="text-xs text-emerald-700"><Zap className="w-3 h-3 inline mr-1" />{gap.fix}</p></div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-sky-500" />Audit-Ready Evidence</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {data.auditReadyEvidence.map((ev, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div className="flex items-center gap-2">
                  {ev.status === 'ready' ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                  <span className="text-sm text-slate-700">{ev.type}</span>
                </div>
                <div className="flex items-center gap-3">
                  {ev.records > 0 && <span className="text-xs text-slate-400">{ev.records} records</span>}
                  <Badge className={`text-[11px] ${ev.status === 'ready' ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30' : 'bg-red-500/10 text-red-600 border-red-500/30'}`}>{ev.status}</Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ComplianceMapper;
